/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file mailPreferences.js
 * @description
 * Pure constants + sanitizer for the in-app mail client's *live* (foreground)
 * auto-refresh cadence. This is the UI poll interval — how often {@link MailContext}
 * re-checks mailbox status / new mail while the app is open — and is intentionally
 * separate from the backend IMAP `sync.pollIntervalMinutes` (which controls how
 * often the server syncs with the mail provider).
 *
 * The cadence is persisted via {@link module:helpers/persistentSettings} under
 * {@link MAIL_POLL_INTERVAL_KEY}. `0` means "Off" (no foreground polling).
 *
 * Mirrors the management refresh-interval helpers in
 * {@link module:helpers/managementApi} so the two interval pickers behave identically.
 */

/** persistentSettings key holding the user's chosen live mail refresh interval (ms). */
export const MAIL_POLL_INTERVAL_KEY = 'mail_poll_interval_ms';

/** Default live mail refresh cadence (matches the admin web client's 20s loop). */
export const MAIL_POLL_DEFAULT_MS = 20000;

/**
 * Selectable live-refresh cadences for the mailbox. `value` is the poll interval
 * in milliseconds; `0` means "Off" (polling disabled). Labels are i18n keys in the
 * mail bundle (`locales/mail/*.json`) resolved at render time.
 * @type {Array<{value: number, labelKey: string}>}
 */
export const MAIL_POLL_PRESETS = [
    {value: 10000, labelKey: 'mail_poll_10s'},
    {value: 20000, labelKey: 'mail_poll_20s'},
    {value: 30000, labelKey: 'mail_poll_30s'},
    {value: 60000, labelKey: 'mail_poll_1m'},
    {value: 300000, labelKey: 'mail_poll_5m'},
    {value: 0, labelKey: 'mail_poll_off'},
];

/**
 * Normalize a raw (possibly persisted/string) live-refresh value into a safe
 * millisecond cadence. Blank/garbage → default; `0` (or negative) → off; in-range
 * positives are kept; out-of-range positives are clamped to the [10s, 5m] bounds.
 * @param {*} raw the raw stored or incoming value.
 * @returns {number} a sane interval in ms (0 = polling disabled).
 */
export const sanitizeMailPollMs = (raw) => {
    if (raw === null || raw === undefined || raw === '') return MAIL_POLL_DEFAULT_MS;
    const n = Number(raw);
    if (Number.isNaN(n)) return MAIL_POLL_DEFAULT_MS;
    if (n <= 0) return 0;
    if (n < 10000) return 10000;
    if (n > 300000) return 300000;
    return n;
};

export default {MAIL_POLL_INTERVAL_KEY, MAIL_POLL_DEFAULT_MS, MAIL_POLL_PRESETS, sanitizeMailPollMs};
